import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface ModalPosition {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  /** 타이틀 하단 부가 설명 */
  subtitle?: string;
  width?: number | string;
  maxWidth?: number | string;
  height?: number | string;
  overflow?: 'visible' | 'hidden' | 'auto';
  showCloseButton?: boolean;
  showOverlay?: boolean;
  /** 배경 블러 처리 (overlay 표시 + backdrop-filter) */
  overlayBlur?: boolean;
  /** 오버레이 z-index (기본 1000, 헤더 모달 등 최상위 표시 시 사용) */
  zIndex?: number;
  /** 모달 위치 (기본: 화면 중앙) */
  position?: ModalPosition;
  /** ModalContainer에 직접 스타일 전달 */
  containerStyle?: React.CSSProperties;
}

const Overlay = styled.div<{ $isOpen: boolean; $showOverlay: boolean; $overlayBlur: boolean; $hasPosition: boolean; $zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props =>
    props.$overlayBlur ? 'rgba(0, 0, 0, 0.25)' :
    props.$showOverlay ? 'rgba(0, 0, 0, 0.5)' : 'transparent'};
  backdrop-filter: ${props => props.$overlayBlur ? 'blur(8px)' : 'none'};
  -webkit-backdrop-filter: ${props => props.$overlayBlur ? 'blur(8px)' : 'none'};
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: ${props => props.$hasPosition ? 'flex-start' : 'center'};
  justify-content: ${props => props.$hasPosition ? 'flex-start' : 'center'};
  z-index: ${props => props.$zIndex ?? 1000};
  padding: ${props => props.$hasPosition ? '0' : props.theme.spacing.lg};
  pointer-events: ${props => (props.$showOverlay || props.$overlayBlur) ? 'auto' : 'none'};
`;

const ModalContainer = styled.div<{ $width?: number | string; $maxWidth?: number | string; $height?: number | string; $overflow?: string; $position?: ModalPosition }>`
  position: ${props => props.$position ? 'absolute' : 'relative'};
  ${props => props.$position?.top != null ? `top: ${props.$position.top};` : ''}
  ${props => props.$position?.right != null ? `right: ${props.$position.right};` : ''}
  ${props => props.$position?.bottom != null ? `bottom: ${props.$position.bottom};` : ''}
  ${props => props.$position?.left != null ? `left: ${props.$position.left};` : ''}
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  width: ${props => {
    if (props.$width == null) return '100%';
    return typeof props.$width === 'number' ? `${props.$width}px` : props.$width;
  }};
  max-width: ${props => {
    if (props.$maxWidth != null) return typeof props.$maxWidth === 'number' ? `${props.$maxWidth}px` : props.$maxWidth;
    if (props.$width != null) return typeof props.$width === 'number' ? `${props.$width}px` : props.$width;
    return '500px';
  }};
  height: ${props => {
    if (props.$height == null) return 'auto';
    return typeof props.$height === 'number' ? `${props.$height}px` : props.$height;
  }};
  max-height: ${props => {
    if (props.$height != null) return typeof props.$height === 'number' ? `${props.$height}px` : props.$height;
    return '90vh';
  }};
  overflow: ${props => props.$overflow || 'auto'};
  border: 1px solid ${props => props.theme.colors.border.ce};
  box-shadow: 10px 20px 24px -4px rgba(10, 13, 18, 0.10), -5px 8px 8px -4px rgba(10, 13, 18, 0.04);
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
  padding: ${props => props.theme.spacing['2xl']};
`;

const ModalHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;
  position: relative;
`;

const ModalTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.gray[900]};
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color6b};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSize['2xl']};
  color: ${props => props.theme.colors.gray[600]};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: background-color 0.2s;
  outline: none;

  html.dark-mode & svg {
    filter: brightness(0) invert(1);
  }
`;

const ModalBody = styled.div<{ $hasFixedHeight?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: ${props => props.$hasFixedHeight ? 'hidden' : 'visible'};
  display: ${props => (props.$hasFixedHeight ? 'flex' : 'block')};
  flex-direction: ${props => (props.$hasFixedHeight ? 'column' : 'unset')};
`;

const Modal: React.FC<ModalProps> = ({
  isOpen, onClose, children, title, subtitle,
  width, maxWidth, height,
  overflow = 'auto',
  showCloseButton = false,
  showOverlay = true,
  overlayBlur = false,
  zIndex,
  position,
  containerStyle,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasFixedHeight = height != null;
  const hasPosition = !!position;
  const showOverlayOrBlur = showOverlay || overlayBlur;
  const modalZIndex = zIndex ?? 1000;

  const modalContent = (
    <Overlay
      $isOpen={isOpen}
      $showOverlay={showOverlay}
      $overlayBlur={overlayBlur}
      $hasPosition={hasPosition}
      $zIndex={modalZIndex}
      onClick={showOverlayOrBlur ? onClose : undefined}
      data-modal
    >
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        data-modal
        $width={width}
        $maxWidth={maxWidth}
        $height={height}
        $overflow={hasFixedHeight ? 'hidden' : overflow}
        $position={position}
        style={{ ...(!showOverlayOrBlur ? { pointerEvents: 'auto' } : {}), ...containerStyle }}
      >
        {(title || subtitle || showCloseButton) && (
          <ModalHeader>
            {title && <ModalTitle>{title}</ModalTitle>}
            {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
            {showCloseButton && onClose && (
              <CloseButton onClick={onClose} aria-label="닫기">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M16 4L4 16" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </CloseButton>
            )}
          </ModalHeader>
        )}
        <ModalBody data-modal $hasFixedHeight={hasFixedHeight}>{children}</ModalBody>
      </ModalContainer>
    </Overlay>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
